import React, { useEffect, useState } from "react";
import {
    addDoc,
    collection,
    limit,
    onSnapshot,
    orderBy,
    query,
    serverTimestamp
} from "firebase/firestore";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { auth, db, signInWithGooglePopup } from "../utils/firebase/firebase.utils";

function ChatSys() {
    const [user, setUser] = useState(null);
    const [isAuthReady, setIsAuthReady] = useState(false);
    const [messages, setMessages] = useState([]);
    const [message, setMessage] = useState("");
    const [error, setError] = useState("");
    const [sending, setSending] = useState(false);

    useEffect(() => onAuthStateChanged(auth, (authenticatedUser) => {
        setUser(authenticatedUser);
        setIsAuthReady(true);
    }), []);

    useEffect(() => {
        if (!user) {
            setMessages([]);
            return undefined; 
        }

        const messagesQuery = query(
            collection(db, "messages"),
            orderBy("createdAt", "desc"),
            limit(100)
        );

        return onSnapshot(messagesQuery, (snapshot) => {
            setMessages(snapshot.docs.map((messageDoc) => ({
                id: messageDoc.id,
                ...messageDoc.data()
            })).reverse());
            setError("");
        }, (snapshotError) => {
            setError(`Unable to load chat: ${snapshotError.message}`);
        });
    }, [user]);

    const handleSignIn = async () => {
        setError("");
        try {
            await signInWithGooglePopup();
        } catch (signInError) {
            setError(`Google sign-in failed: ${signInError.message}`);
        }
    };

    const handleSend = async (event) => {
        event.preventDefault();
        const text = message.trim();
        if (!text || !user || sending) return;

        setSending(true);
        setError("");
        try {
            await addDoc(collection(db, "messages"), {
                text,
                senderId: user.uid,
                displayName: user.displayName || user.email || "Google user",
                createdAt: serverTimestamp()
            });
            setMessage("");
        } catch (sendError) {
            setError(`Unable to send message: ${sendError.message}`);
        } finally {
            setSending(false);  
        }
    };

    const handleSignOut = async () => {
        setError("");
        try {
            await signOut(auth);
        } catch (signOutError) {
            setError(`Unable to sign out: ${signOutError.message}`);
        }
    };

    const formatTime = (timestamp) => {
        if (!timestamp || typeof timestamp.toDate !== 'function') return 'Sending…';
        return timestamp.toDate().toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit"
        });
    };

    return (
        <section className="page-section chat-page-section" aria-labelledby="chat-section-heading">
            <div className="container">
                <div className="chat-layout">
                    <aside className="chat-project-details">
                        <h2 id="chat-section-heading">Sign-in with Google</h2>
                        <p>
                            I added Google sign-in before loading the chat, then subscribed to
                            Firestore so new messages appear without refreshing the page.
                        </p>
                        <div className="chat-signin-area">
                            {!isAuthReady ? (
                                <p className="chat-auth-loading" role="status">Checking your sign-in…</p>
                            ) : user ? (
                                <div className="chat-user-card">
                                    <span className="chat-user-avatar" aria-hidden="true">
                                        {(user.displayName || user.email || 'U').charAt(0).toUpperCase()}
                                    </span>
                                    <div className="chat-user-details">
                                        <span className="chat-user-label">Authenticated account</span>
                                        <strong>{user.displayName || user.email}</strong>
                                    </div>
                                    <button className="btn btn-outline-secondary" type="button" onClick={handleSignOut}>
                                        Sign out
                                    </button>
                                </div>
                            ) : (
                                <button className="btn btn-primary chat-signin-button" type="button" onClick={handleSignIn}>
                                    <i className="fab fa-google" aria-hidden="true"></i>
                                    Authenticate with Google
                                </button>
                            )}
                        </div>
                    </aside>

                    <div className="chat-panel">
                        <div className="chat-panel-header">
                            <div>
                                <h3>Live Chat</h3>
                                <p>Showing newest 100 messages</p>
                            </div>
                            <span className={`chat-live-indicator${user ? ' is-online' : ''}`}>
                                <span aria-hidden="true"></span>
                                {user ? 'Listening' : 'Authentication required'}
                            </span>
                        </div>

                        {error && <p className="chat-error" role="alert">{error}</p>}

                        <div className="chat-transcript" id="chatbox" role="log" aria-label="Chat messages" aria-live="polite" aria-relevant="additions">
                            {!isAuthReady ? (
                                <div className="chat-empty-state" role="status">
                                    <span className="chat-empty-icon"><i className="fas fa-spinner fa-spin" aria-hidden="true"></i></span>
                                    <p>Getting the chat ready…</p>
                                </div>
                            ) : !user ? (
                                <div className="chat-empty-state">
                                    <span className="chat-empty-icon"><i className="fas fa-lock" aria-hidden="true"></i></span>
                                    <p>Please sign in first to see the chat!</p>
                                </div>
                            ) : messages.length === 0 && !error ? (
                                <div className="chat-empty-state">
                                    <span className="chat-empty-icon"><i className="far fa-comment-dots" aria-hidden="true"></i></span>
                                    <p>No documents yet. Submit a message to create the first one.</p>
                                </div>
                            ) : (
                                messages.map((chatMessage) => {
                                    const isOwnMessage = chatMessage.senderId === user.uid;
                                    const timestamp = chatMessage.createdAt?.toDate?.();

                                    return (
                                        <article
                                            key={chatMessage.id}
                                            className={`chat-message${isOwnMessage ? ' chat-message-own' : ''}`}
                                        >
                                            <div className="chat-message-heading">
                                                <strong>{isOwnMessage ? 'You' : chatMessage.displayName || 'Google user'}</strong>
                                                <time dateTime={timestamp?.toISOString()}>
                                                    {formatTime(chatMessage.createdAt)}
                                                </time>
                                            </div>
                                            <p>{chatMessage.text}</p>
                                        </article>
                                    );
                                })
                            )}
                        </div>

                        {user ? (
                            <form onSubmit={handleSend} className="chat-compose">
                                <label className="visually-hidden" htmlFor="chatMessage">Your message</label>
                                <input
                                    className="form-control"
                                    id="chatMessage"
                                    type="text"
                                    value={message}
                                    onChange={(event) => setMessage(event.target.value)}
                                    placeholder="Send a message..."
                                    maxLength={1000}
                                    required
                                />
                                <button className="btn btn-primary" type="submit" disabled={sending || !message.trim()}>
                                    <span>{sending ? 'Sending…' : 'Send'}</span>
                                    <i className="fas fa-paper-plane" aria-hidden="true"></i>
                                </button>
                            </form>
                        ) : (
                            <p className="chat-compose-hint">Please sign in to send a message!</p>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
}

export default ChatSys;